import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import './tariff-configuration.scss';

const peso = (v, d = 2) => `₱${Number(v).toFixed(d)}`;
const BLUE = { color: '#005293' };

const EMPTY = {
  code: '', name: '', sessionFee: '50.00', energyRate: '18.50', parkingRate: '0.00',
  idleFee: '5.00', gracePeriod: '15', reservationFee15: '30.00', reservationFee30: '50.00',
};

function TariffModal({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial ? {
    code: initial.code, name: initial.name, sessionFee: String(initial.sessionFee), energyRate: String(initial.energyRate),
    parkingRate: String(initial.parkingRate), idleFee: String(initial.idleFee), gracePeriod: String(initial.gracePeriod),
    reservationFee15: String(initial.reservationFee15), reservationFee30: String(initial.reservationFee30),
  } : EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  // Sites this tariff applies at. null = the list could not be loaded, so assignments are left untouched.
  const [sites, setSites] = useState([]);
  const [siteIds, setSiteIds] = useState(initial ? null : []);
  useEffect(() => {
    let live = true;
    api.listNetworkSites().then((list) => live && setSites(Array.isArray(list) ? list : [])).catch(() => live && setSites([]));
    if (initial) {
      api.getTariffSites(initial.id)
        .then((list) => live && setSiteIds((Array.isArray(list) ? list : []).map((x) => x.id || x.site_id || x.siteId).filter(Boolean)))
        .catch(() => live && setSiteIds(null));
    }
    return () => { live = false; };
  }, [initial]);
  const toggleSite = (id) => setSiteIds((cur) => ((cur || []).includes(id) ? cur.filter((x) => x !== id) : [...(cur || []), id]));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inv = (k) => (errors[k] ? 'is-invalid' : '');

  const submit = async () => {
    const next = {};
    if (!f.code.trim()) next.code = 'Tariff code is required.';
    if (!f.name.trim()) next.name = 'Display name is required.';
    [['sessionFee', 'Session fee'], ['energyRate', 'Energy rate'], ['parkingRate', 'Parking rate'], ['idleFee', 'Idle fee'],
      ['gracePeriod', 'Grace period'], ['reservationFee15', '15-minute reservation fee'], ['reservationFee30', '30-minute reservation fee']].forEach(([k, label]) => {
      const v = f[k];
      if (v === '' || Number.isNaN(Number(v)) || Number(v) < 0) next[k] = `${label} must be a non-negative number.`;
    });
    if (!next.gracePeriod && !Number.isInteger(Number(f.gracePeriod))) next.gracePeriod = 'Grace period must be a whole number of minutes.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    setServerError('');
    try {
      await onSave({
        code: f.code.trim(), name: f.name.trim(), currency: 'PHP',
        sessionFee: Number(f.sessionFee), energyRate: Number(f.energyRate), parkingRate: Number(f.parkingRate),
        idleFee: Number(f.idleFee), gracePeriod: Number(f.gracePeriod),
        reservationFee15: Number(f.reservationFee15), reservationFee30: Number(f.reservationFee30),
        effectiveFrom: initial?.effectiveFrom ?? null, effectiveTo: initial?.effectiveTo ?? null,
        isActive: initial?.isActive,
      }, siteIds);
    } catch (err) {
      setServerError(err.message || 'Could not save the tariff.');
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block tc-modal-backdrop" id="modal-tariff" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="modal-tariff-title">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content shadow-lg">
          <div className="modal-header">
            <h5 className="modal-title fw-bold" id="modal-tariff-title" style={BLUE}>
              <i className="fas fa-tags me-2" style={BLUE} aria-hidden="true" />Configure Tariff Schedule
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
          </div>
          <div className="modal-body p-4">
            {serverError && <div className="alert alert-danger py-2" id="tariff-error" role="alert">{serverError}</div>}
            <form id="form-tariff" onSubmit={(e) => e.preventDefault()} noValidate>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold" htmlFor="t-code">Tariff Code Identifier</label>
                  <input type="text" className={`form-control font-monospace ${inv('code')}`} id="t-code" required placeholder="TARIFF-DC-FAST" value={f.code} onChange={set('code')} />
                  <div className="invalid-feedback">{errors.code}</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold" htmlFor="t-name">Display Name</label>
                  <input type="text" className={`form-control ${inv('name')}`} id="t-name" required placeholder="DC Fast Charging Highway Tier" value={f.name} onChange={set('name')} />
                  <div className="invalid-feedback">{errors.name}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label small fw-bold" htmlFor="t-session">Session Initiation Fee (₱)</label>
                  <input type="number" step="0.5" min="0" className={`form-control ${inv('sessionFee')}`} id="t-session" required value={f.sessionFee} onChange={set('sessionFee')} />
                  <div className="invalid-feedback">{errors.sessionFee}</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold" htmlFor="t-energy">Energy Rate per kWh (₱)</label>
                  <input type="number" step="0.25" min="0" className={`form-control ${inv('energyRate')}`} id="t-energy" required value={f.energyRate} onChange={set('energyRate')} />
                  <div className="invalid-feedback">{errors.energyRate}</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold" htmlFor="t-parking">Parking Bay Rate / Hr (₱)</label>
                  <input type="number" step="1.0" min="0" className={`form-control ${inv('parkingRate')}`} id="t-parking" required value={f.parkingRate} onChange={set('parkingRate')} />
                  <div className="invalid-feedback">{errors.parkingRate}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold" htmlFor="t-idle">Idle Overstay Fee per Minute (₱)</label>
                  <input type="number" step="0.5" min="0" className={`form-control ${inv('idleFee')}`} id="t-idle" required value={f.idleFee} onChange={set('idleFee')} />
                  <div className="invalid-feedback">{errors.idleFee}</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold" htmlFor="t-grace">Idle Grace Period (Minutes)</label>
                  <input type="number" min="0" className={`form-control ${inv('gracePeriod')}`} id="t-grace" required value={f.gracePeriod} onChange={set('gracePeriod')} />
                  <div className="invalid-feedback">{errors.gracePeriod}</div>
                </div>
              </div>
              <div className="p-3 rounded bg-light border mb-3">
                <h6 className="fw-bold mb-2" style={BLUE}>
                  <i className="fas fa-bookmark me-1" style={{ color: '#d97706' }} aria-hidden="true" />Connector Reservation Fees (Fixed Duration)
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold" htmlFor="t-res-15">15-Minute Reservation Holding Fee (₱)</label>
                    <input type="number" step="1.0" min="0" className={`form-control ${inv('reservationFee15')}`} id="t-res-15" required value={f.reservationFee15} onChange={set('reservationFee15')} />
                    <div className="invalid-feedback">{errors.reservationFee15}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold" htmlFor="t-res-30">30-Minute Reservation Holding Fee (₱)</label>
                    <input type="number" step="1.0" min="0" className={`form-control ${inv('reservationFee30')}`} id="t-res-30" required value={f.reservationFee30} onChange={set('reservationFee30')} />
                    <div className="invalid-feedback">{errors.reservationFee30}</div>
                  </div>
                </div>
              </div>
              <div className="mt-4" id="tariff-sites">
                <h6 className="fw-bold mb-1" style={BLUE}><i className="fas fa-map-marker-alt me-2" aria-hidden="true" />Applies at these sites</h6>
                <p className="text-muted small mb-2">A tariff only takes effect at the sites ticked here. A site with no tariff bills the network default rate.</p>
                {sites.length === 0 && <div className="text-muted small">No sites could be loaded.</div>}
                {siteIds === null && sites.length > 0 && <div className="text-muted small">Current site assignments could not be loaded and will be left unchanged.</div>}
                {siteIds !== null && sites.length > 0 && (
                  <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {sites.map((st) => {
                      const sid = st.id;
                      return (
                        <div className="form-check" key={sid}>
                          <input className="form-check-input" type="checkbox" id={`t-site-${sid}`} checked={siteIds.includes(sid)} onChange={() => toggleSite(sid)} />
                          <label className="form-check-label small" htmlFor={`t-site-${sid}`}>{st.site_name || st.name} <span className="text-muted">— {st.city} ({st.site_code || st.code})</span></label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" id="btn-save-tariff" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save Tariff Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TariffConfigurationPage() {
  const [tariffs, setTariffs] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | tariff

  const [simId, setSimId] = useState('');
  const [kwh, setKwh] = useState(30);
  const [idle, setIdle] = useState(10);
  const [includeRes, setIncludeRes] = useState(false);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(''), 6000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.listTariffs('page=1&pageSize=50');
      setTariffs(res.data);
      setActiveCount(res.meta.activeCount);
    } catch (e) {
      setError(e.message || 'Could not load tariffs.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sim = useMemo(() => {
    const t = tariffs.find((x) => x.id === simId) || tariffs[0];
    if (!t) return null;
    const session = t.sessionFee;
    const energy = kwh * t.energyRate;
    const idleCost = idle * t.idleFee;
    const holding = includeRes ? t.reservationFee30 : 0;
    return { t, session, energy, idleCost, holding, total: session + energy + idleCost + holding };
  }, [tariffs, simId, kwh, idle, includeRes]);

  const save = async (body, siteIds) => {
    let id;
    if (editing && editing !== 'new') {
      await api.updateTariff(editing.id, body);
      id = editing.id;
    } else {
      id = (await api.createTariff(body))?.id;
    }
    // siteIds is null when the current assignments could not be loaded: leave them as they are.
    if (id && Array.isArray(siteIds)) await api.assignTariffSites(id, siteIds);
    flash('Tariff schedule saved successfully.');
    setEditing(null);
    load();
  };

  return (
    <div className="container-fluid py-4" id="tariff-config-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={BLUE}>
            <i className="fas fa-tags me-2" style={BLUE} aria-hidden="true" />Multi-Component Tariff Schedules &amp; Reservation Fees
          </h4>
          <p className="text-muted small mb-0">Configure session initiation fees, energy rates, parking occupancy, idle penalties, and connector reservation holding fees.</p>
        </div>
        <div className="mt-2 mt-md-0">
          <button className="btn btn-sm btn-primary" id="btn-new-tariff" onClick={() => setEditing('new')}>
            <i className="fas fa-plus me-1" aria-hidden="true" /> New Tariff Schedule
          </button>
        </div>
      </div>

      {notice && <div className="alert alert-success py-2" id="tariff-notice" role="status">{notice}</div>}
      {error && <div className="alert alert-danger py-2" id="tariff-page-error" role="alert">{error}</div>}

      <div className="row">
        <div className="col-12 col-xl-8 mb-4">
          <div className="card border shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}>
                <i className="fas fa-list-alt me-2" style={BLUE} aria-hidden="true" />Active Tariff Plans
              </h6>
              <span className="badge bg-success fw-bold" id="active-plan-count">{activeCount} Active Plans</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr className="text-muted small text-uppercase">
                      <th>Tariff Code &amp; Name</th>
                      <th>Session Fee</th>
                      <th>Energy Rate</th>
                      <th>Parking / Hr</th>
                      <th>Idle Fee</th>
                      <th>15m / 30m Hold Fee</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody id="tariffs-table-body">
                    {loading && <tr><td colSpan={8} className="text-center py-4 text-muted">Loading…</td></tr>}
                    {!loading && tariffs.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-muted">No tariff schedules configured.</td></tr>}
                    {!loading && tariffs.map((t) => (
                      <tr key={t.id} data-tariff-id={t.id}>
                        <td>
                          <div className="fw-bold font-monospace text-primary">{t.code}</div>
                          <small className="text-muted">{t.name}</small>
                        </td>
                        <td className="font-monospace text-dark fw-bold">{peso(t.sessionFee)}</td>
                        <td className="font-monospace text-primary fw-bold">{peso(t.energyRate)}/kWh</td>
                        <td className="font-monospace text-dark fw-bold">{peso(t.parkingRate)}</td>
                        <td className="font-monospace text-warning fw-bold">{peso(t.idleFee)}/m ({t.gracePeriod}m grace)</td>
                        <td className="font-monospace text-success fw-bold">{peso(t.reservationFee15, 0)} / {peso(t.reservationFee30, 0)}</td>
                        <td><span className={`badge ${t.isActive ? 'badge-available' : 'badge-secondary'}`}>{t.isActive ? 'Active' : 'Disabled'}</span></td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary btn-edit-tariff" aria-label={`Edit ${t.code}`} onClick={() => setEditing(t)}>
                            <i className="fas fa-edit" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4 mb-4">
          <div className="card border shadow-sm h-100">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}>
                <i className="fas fa-calculator me-2" style={{ color: '#0284c7' }} aria-hidden="true" />Live Tariff Bill Simulator
              </h6>
            </div>
            <div className="card-body p-4">
              <div className="mb-3">
                <label className="small text-muted fw-bold mb-1" htmlFor="sim-tariff-select">Select Applied Tariff</label>
                <select className="form-select form-select-sm" id="sim-tariff-select" value={sim?.t.id || ''} onChange={(e) => setSimId(e.target.value)}>
                  {tariffs.map((t) => <option key={t.id} value={t.id}>{t.code} ({t.name})</option>)}
                </select>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <label className="fw-bold mb-0" htmlFor="sim-kwh">Energy Consumed:</label>
                  <strong className="text-dark fw-bold" id="sim-kwh-val">{kwh} kWh</strong>
                </div>
                <input type="range" className="form-range" id="sim-kwh" min="5" max="100" value={kwh} onChange={(e) => setKwh(Number(e.target.value))} />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <label className="fw-bold mb-0" htmlFor="sim-idle">Idle Overstay (Beyond Grace):</label>
                  <strong className="text-dark fw-bold" id="sim-idle-val">{idle} mins</strong>
                </div>
                <input type="range" className="form-range" id="sim-idle" min="0" max="60" value={idle} onChange={(e) => setIdle(Number(e.target.value))} />
              </div>
              <div className="form-check mb-3">
                <input type="checkbox" className="form-check-input" id="sim-include-res" checked={includeRes} onChange={(e) => setIncludeRes(e.target.checked)} />
                <label className="form-check-label small fw-bold text-dark" htmlFor="sim-include-res">
                  Include 30-min Connector Reservation Fee
                </label>
              </div>

              <div className="bg-light rounded p-3 border mt-3">
                <h6 className="text-muted small text-uppercase mb-3 fw-bold">Simulated Bill Breakdown</h6>
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Session Initiation:</span>
                  <span className="fw-bold text-dark font-monospace" id="sim-res-session">{peso(sim?.session ?? 0)}</span>
                </div>
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Energy Charges:</span>
                  <span className="fw-bold text-dark font-monospace" id="sim-res-energy">{peso(sim?.energy ?? 0)}</span>
                </div>
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Idle Penalty:</span>
                  <span className="text-danger fw-bold font-monospace" id="sim-res-idle">{peso(sim?.idleCost ?? 0)}</span>
                </div>
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Reservation Holding Fee:</span>
                  <span className="fw-bold font-monospace" style={{ color: '#d97706' }} id="sim-res-holding">{peso(sim?.holding ?? 0)}</span>
                </div>
                <div className="border-top pt-2 d-flex justify-content-between fw-bold">
                  <span className="text-dark">Estimated Total:</span>
                  <span className="h5 mb-0 font-monospace fw-bold" style={BLUE} id="sim-res-total">{peso(sim?.total ?? 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && <TariffModal initial={editing === 'new' ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}
